/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {Crumb} from '@ion/core/src/types/entities';
import {GlobalGroupProps} from '@ion/core/src/types/general';
import {useDrawingComplete} from '../hooks/useDrawingComplete';
import CreateShapesBoundaries from './CreateShapesBoundaries';
import {GeofenceSearch} from './GeofenceSearch';
import SaveSettings from './SaveSettings';

type DrawGeoFenceProps = GlobalGroupProps & {
  map: __esri.Map;
  completeDrawing: Function;
  view: __esri.MapView;
  isVisible: boolean;
  assetCategories?: string[];
  geofenceSave?: Function;
  selectCrumbs?: Function;
  crumbs?: Crumb[];
  savingGeofence?: boolean;
  mapViewEl?: HTMLElement;
  geoFencesGraphicsLayerRef?: {
    current: null | __esri.Layer;
  };
  sketchViewModelRef?: {
    current: null | __esri.Layer;
  };
  selectedMapPointRef?: {
    current: null | __esri.Layer;
  };
  cancelDrawing?: Function;
  editGeofenceGraphic?: __esri.Graphic;
  mapClickHandler?: any;
  setMapClickHandler?: (val: any) => void;
  handleBack?: Function;
  closeGeoFencePanel?: Function;
  handleApplyBoundary?: Function;
  isBoundary?: boolean;
  useHereApi?: boolean;
  isPhone?: boolean;
  isFMTracking?: boolean;
  toggleShowBoundaryPanel?: Function;
};

export function DrawGeoFence({
  map,
  completeDrawing,
  view,
  isVisible,
  globalGroup,
  accountMap,
  assetCategories,
  geofenceSave,
  selectCrumbs,
  crumbs,
  savingGeofence,
  mapViewEl,
  geoFencesGraphicsLayerRef,
  sketchViewModelRef,
  selectedMapPointRef,
  editGeofenceGraphic,
  cancelDrawing,
  mapClickHandler,
  setMapClickHandler,
  handleBack,
  closeGeoFencePanel,
  handleApplyBoundary,
  toggleShowBoundaryPanel,
  isBoundary,
  isPhone,
  useHereApi = false,
  isFMTracking = false
}: DrawGeoFenceProps) {
  const [drawingComplete, setDrawingComplete] = useState(false);
  const [finishedDrawing, onCompleteDrawing]  =
    useDrawingComplete(isBoundary, handleApplyBoundary, !!editGeofenceGraphic);
  const [finalDrawingDetails, setFinalDrawingDetails] = useState(null);

  useEffect(() => {
    if (finalDrawingDetails) completeDrawing(finalDrawingDetails);
  }, [finalDrawingDetails]);

  return (
    <div>
      {finishedDrawing ? (
        <SaveSettings assetCategories={assetCategories}
          accountMap={accountMap}
          globalGroup={globalGroup}
          finalDrawingDetails={finalDrawingDetails}
          geofenceSave={geofenceSave}
          selectCrumbs={selectCrumbs}
          closeGeoFencePanel={closeGeoFencePanel}
          handleBack={handleBack}
          crumbs={crumbs}
          editGeofenceGraphic={editGeofenceGraphic}
          savingGeofence={savingGeofence}
          geoFencesGraphicsLayerRef={geoFencesGraphicsLayerRef}
          sketchViewModelRef={sketchViewModelRef}
          selectedMapPointRef={selectedMapPointRef}
          map={map}
          mapClickHandler={mapClickHandler} />
      ) : (
        <GeofenceSearch view={view}
          map={map}
          useHereApi={useHereApi}
          isVisible={isVisible}
          drawingComplete={drawingComplete}
          handleBack={handleBack}
          toggleShowBoundaryPanel={toggleShowBoundaryPanel}
          crumbs={crumbs}
          selectCrumbs={selectCrumbs}
          geofenceType={translate('maps:geofences.draw')}
          handleCompleteDrawing={onCompleteDrawing}
          mapClickHandler={mapClickHandler}
          isPhone={isPhone} />
      )}
      <CreateShapesBoundaries map={map}
        mapView={view}
        isFMTracking={isFMTracking}
        setDrawingComplete={setDrawingComplete}
        setFinalDrawingDetails={setFinalDrawingDetails}
        finishedDrawing={finishedDrawing}
        mapViewEl={mapViewEl}
        editGeofenceGraphic={editGeofenceGraphic}
        geoFencesGraphicsLayerRef={geoFencesGraphicsLayerRef}
        sketchViewModelRef={sketchViewModelRef}
        cancelDrawing={cancelDrawing}
        mapClickHandler={mapClickHandler}
        setMapClickHandler={setMapClickHandler} />
    </div>
  );
}
